/**
 * B2BModal — B2B 아웃바운드 접수 모달 (new design)
 *
 * preview/index.html 의 .modal-backdrop 1:1 매칭
 *  - 상단: 서비스 복수 체크박스 (1개 이상 필수)
 *  - 회사명/대표자명/연락처/사업자등록번호 필수
 *  - 이메일/직원수/접촉경로 선택
 *  - 파일 첨부: 드래그앤드롭 + 클릭. 최대 5개, 파일당 10MB 이하
 *  - 메모/개인정보 동의
 *
 * 데이터 흐름:
 *  1) 폼 검증 (zod) — 사업자번호 체크섬까지
 *  2) submitInquiry() 로 inquiry 문서 생성 → id 발급
 *  3) 파일이 있으면 Firebase Storage 병렬 업로드 → inquiry 문서에 attachments 업데이트
 *  4) 성공/실패 토스트
 *
 * Storage 경로/확장자 정책은 storage.rules 와 일치해야 함.
 */
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  SERVICE_TYPES,
  SERVICE_LIST,
  B2B_SOURCE_OPTIONS,
  type ServiceType,
} from '@/config/constants';
import { submitInquiry, ApiError } from '@/api/client';
import { useToast } from '@/hooks/useToast';
import { useScrollLock } from '@/hooks/useScrollLock';
import { useBusinessVerify } from '@/hooks/useBusinessVerify';
import { formatPhone, formatBusinessNumber } from '@/utils/format';
import { isValidBusinessNumberChecksum } from '@/utils/businessNumber';
import type { InquiryPayload } from '@/types/inquiry';

// ================================================================
// 파일 업로드 정책 (storage.rules 와 일치)
// ================================================================
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;
const ALLOWED_EXTENSIONS = [
  '.pdf',
  '.png', '.jpg', '.jpeg', '.gif', '.webp',
  '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.hwp', '.hwpx',
  '.zip',
  '.txt',
];
/** input[type=file] 의 accept 속성용 문자열 */
const ACCEPT_ATTR = ALLOWED_EXTENSIONS.join(',');

function hasAllowedExtension(name: string): boolean {
  const lower = name.toLowerCase();
  return ALLOWED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

/** 파일명 안전화: 한글/특수문자 제거 → Firebase Storage 경로용 */
function sanitizeFileName(name: string): string {
  const lastDot = name.lastIndexOf('.');
  const base = lastDot > 0 ? name.slice(0, lastDot) : name;
  const ext = lastDot > 0 ? name.slice(lastDot) : '';
  const safe = base.replace(/[^a-zA-Z0-9_\-.]/g, '_').slice(0, 50);
  const safeBase = safe || 'file_' + Math.random().toString(36).slice(2, 8);
  return safeBase + ext;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
}

// ================================================================
// 폼 Zod 스키마
// ================================================================
const b2bFormSchema = z.object({
  services: z
    .array(z.enum(SERVICE_TYPES))
    .min(1, '상담 서비스를 1개 이상 선택해 주세요'),
  companyName: z.string().min(1, '회사명을 입력해 주세요').max(100),
  representativeName: z.string().min(1, '대표자명을 입력해 주세요').max(50),
  contactPhone: z
    .string()
    .min(1, '연락처를 입력해 주세요')
    .regex(/^010-?\d{4}-?\d{4}$/, '올바른 휴대폰 번호 형식이 아닙니다'),
  contactEmail: z
    .string()
    .email('올바른 이메일 주소를 입력해 주세요')
    .optional()
    .or(z.literal('')),
  businessNumber: z
    .string()
    .regex(/^\d{3}-?\d{2}-?\d{5}$/, '사업자등록번호 10자리를 정확히 입력해 주세요')
    .refine((v) => isValidBusinessNumberChecksum(v), {
      message: '유효하지 않은 사업자등록번호입니다',
    }),
  employeeCount: z.string().max(50).optional().or(z.literal('')),
  contactSource: z.string().max(50).optional().or(z.literal('')),
  memo: z.string().max(2000).optional().or(z.literal('')),
  privacyConsent: z.literal(true, {
    errorMap: () => ({ message: '개인정보 수집·이용에 동의해 주세요' }),
  }),
});

type B2BFormValues = z.infer<typeof b2bFormSchema>;

// ================================================================
// 파일별 업로드 상태
// ================================================================
type UploadStatus = 'pending' | 'uploading' | 'done' | 'error';
interface UploadState {
  progress: number;
  status: UploadStatus;
}

interface AttachmentMeta {
  fileName: string;
  filePath: string;
  fileSize: number;
  uploadedAt: string;
}

// ================================================================
// Firebase Storage 병렬 업로드 (uploadBytesResumable)
// 기존 B2BModal과 동일한 패턴
// ================================================================
async function uploadFilesParallel(
  inquiryId: string,
  files: File[],
  onProgress: (idx: number, next: UploadState) => void
): Promise<{ success: AttachmentMeta[]; failedNames: string[] }> {
  if (files.length === 0) return { success: [], failedNames: [] };

  // [C04] 앱 초기화는 config/firebase.ts 단일 진입점으로 통일.
  // 직접 initializeApp을 부르면 App Check 토큰 없이 업로드가 나간다.
  const [storageMod, { getStorageInstance }] = await Promise.all([
    import('firebase/storage'),
    import('@/config/firebase'),
  ]);
  const { ref, uploadBytesResumable } = storageMod;
  const storage = await getStorageInstance();

  const tasks = files.map(
    (f, idx) =>
      new Promise<AttachmentMeta>((resolve, reject) => {
        const safeName = sanitizeFileName(f.name);
        const filePath = `inquiry-attachments/${inquiryId}/${Date.now()}_${idx}_${safeName}`;
        const r = ref(storage, filePath);

        onProgress(idx, { progress: 0, status: 'uploading' });

        const task = uploadBytesResumable(r, f, {
          contentType: f.type || 'application/octet-stream',
        });

        task.on(
          'state_changed',
          (snap) => {
            const pct =
              snap.totalBytes > 0
                ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                : 0;
            onProgress(idx, { progress: pct, status: 'uploading' });
          },
          (err) => {
            onProgress(idx, { progress: 0, status: 'error' });
            reject(err);
          },
          () => {
            onProgress(idx, { progress: 100, status: 'done' });
            resolve({
              fileName: f.name,
              filePath,
              fileSize: f.size,
              uploadedAt: new Date().toISOString(),
            });
          }
        );
      })
  );

  const results = await Promise.allSettled(tasks);
  const success: AttachmentMeta[] = [];
  const failedNames: string[] = [];
  results.forEach((r, idx) => {
    if (r.status === 'fulfilled') success.push(r.value);
    else failedNames.push(files[idx]?.name ?? `file_${idx}`);
  });
  return { success, failedNames };
}

/** inquiry 문서 attachments 필드 업데이트 */
async function updateInquiryAttachments(
  inquiryId: string,
  attachments: AttachmentMeta[]
): Promise<void> {
  const [{ getDb }, firestore] = await Promise.all([
    import('@/config/firebase'),
    import('firebase/firestore'),
  ]);
  const { doc, updateDoc, serverTimestamp } = firestore;
  const db = await getDb();
  await updateDoc(doc(db, 'inquiries', inquiryId), {
    attachments,
    updatedAt: serverTimestamp(),
  });
}

// ================================================================
// Props
// ================================================================
export interface B2BModalProps {
  open: boolean;
  onClose: () => void;
  /** 개인정보 동의 링크 클릭 → 모달 또는 페이지 */
  onOpenPrivacy?: () => void;
}

export function B2BModal({ open, onClose, onOpenPrivacy }: B2BModalProps) {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadStates, setUploadStates] = useState<UploadState[]>([]);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // body 스크롤 잠금
  useScrollLock(open);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<B2BFormValues>({
    resolver: zodResolver(b2bFormSchema),
    defaultValues: {
      services: [],
      companyName: '',
      representativeName: '',
      contactPhone: '',
      contactEmail: '',
      businessNumber: '',
      employeeCount: '',
      contactSource: '',
      memo: '',
      privacyConsent: false as unknown as true,
    },
  });

  const servicesValue = watch('services');
  const businessNumberValue = watch('businessNumber');

  // 사업자번호 국세청 진위확인 (blur 시 호출)
  const bizVerify = useBusinessVerify(businessNumberValue);

  // ESC 닫기 (제출 중에는 막음)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  // ----------------------------------------------------------------
  // 필드 핸들러
  // ----------------------------------------------------------------
  const toggleService = (id: ServiceType) => {
    const cur = new Set(servicesValue);
    if (cur.has(id)) cur.delete(id);
    else cur.add(id);
    setValue('services', Array.from(cur), { shouldValidate: true });
  };

  const onPhoneChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setValue('contactPhone', formatPhone(e.target.value));
  };
  const onBizChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setValue('businessNumber', formatBusinessNumber(e.target.value));
  };

  // ----------------------------------------------------------------
  // 파일 처리
  // ----------------------------------------------------------------
  const addFiles = (incoming: FileList | File[]) => {
    const newOnes = Array.from(incoming);
    const next: File[] = [...files];
    const errs: string[] = [];

    for (const f of newOnes) {
      if (next.length >= MAX_FILES) {
        errs.push(`파일은 최대 ${MAX_FILES}개까지 첨부 가능합니다`);
        break;
      }
      if (f.size > MAX_FILE_SIZE) {
        errs.push(`"${f.name}" 파일이 10MB를 초과합니다`);
        continue;
      }
      if (!hasAllowedExtension(f.name)) {
        errs.push(`"${f.name}" 은(는) 허용되지 않는 파일 형식입니다`);
        continue;
      }
      next.push(f);
    }

    setFiles(next);
    setUploadStates(next.map(() => ({ progress: 0, status: 'pending' as UploadStatus })));

    // 첫 번째 에러만 토스트로 (중복 노이즈 방지)
    if (errs.length > 0) {
      showToast({ type: 'error', message: errs[0]! });
    }
  };

  const onFilePick: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = '';
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setUploadStates((prev) => prev.filter((_, i) => i !== idx));
  };

  // 드래그 이벤트 (브라우저 기본 동작 차단 필수)
  const onDragOver: React.DragEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };
  const onDragLeave: React.DragEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };
  const onDrop: React.DragEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  // ----------------------------------------------------------------
  // 닫기 (제출 중에는 막음 + 폼/파일 초기화)
  // ----------------------------------------------------------------
  const handleClose = () => {
    if (submitting) return;
    onClose();
    // 다음 오픈을 위해 폼 초기화
    setTimeout(() => {
      reset();
      setFiles([]);
      setUploadStates([]);
    }, 250);
  };

  // ----------------------------------------------------------------
  // 제출
  // ----------------------------------------------------------------
  const onSubmit = async (data: B2BFormValues) => {
    if (submitting) return;

    // 사업자번호 진위확인이 실패한 상태면 제출 차단
    // (not_found, closed, mismatch 모두 isError === true)
    if (bizVerify.isError) {
      showToast({
        type: 'error',
        message:
          bizVerify.status === 'not_found'
            ? '국세청에 등록되지 않은 사업자번호입니다. 다시 확인해 주세요.'
            : bizVerify.status === 'closed'
            ? '폐업 처리된 사업자번호입니다. 다시 확인해 주세요.'
            : bizVerify.message || '사업자 정보를 다시 확인해 주세요.',
      });
      return;
    }

    setSubmitting(true);

    try {
      // 메인 serviceType은 첫 번째, 전체 라벨은 memo 앞에 합쳐 보냄
      const mainServiceId = data.services[0] as ServiceType;
      const labels = data.services
        .map((id) => SERVICE_LIST.find((s) => s.id === id)?.label ?? id)
        .join(', ');
      const memoMerged = `[관심 서비스: ${labels}]\n${
        data.contactSource ? `[접촉 경로: ${data.contactSource}]\n` : ''
      }${data.memo ?? ''}`.trim();

      const payload: InquiryPayload = {
        channel: 'outbound',
        serviceType: mainServiceId,
        serviceTypes: data.services as ServiceType[],
        companyName: data.companyName,
        representativeName: data.representativeName,
        contactPhone: data.contactPhone,
        contactEmail: data.contactEmail || undefined,
        businessNumber: data.businessNumber,
        employeeCount: data.employeeCount || undefined,
        contactSource: data.contactSource || undefined,
        memo: memoMerged,
        privacyConsent: true,
      };

      // 1) inquiry 문서 생성 → id 발급
      const res = await submitInquiry(payload);

      // 2) 파일이 있으면 Storage 업로드 → attachments 업데이트
      if (files.length > 0) {
        const { success, failedNames } = await uploadFilesParallel(
          res.id,
          files,
          (idx, next) => {
            setUploadStates((prev) => {
              const copy = [...prev];
              copy[idx] = next;
              return copy;
            });
          }
        );

        if (success.length > 0) {
          try {
            await updateInquiryAttachments(res.id, success);
          } catch {
            // 업데이트 실패해도 inquiry 자체는 접수됐으므로 안내만
            showToast({
              type: 'error',
              message: '첨부 파일 정보 저장에 일부 실패했습니다. 담당자가 직접 확인해드립니다.',
            });
          }
        }

        if (failedNames.length > 0) {
          showToast({
            type: 'error',
            message: `일부 파일 업로드 실패: ${failedNames.join(', ')}`,
          });
        }
      }

      showToast({
        type: 'success',
        message: 'B2B 접수가 완료되었습니다. 영업일 24시간 이내에 연락드리겠습니다.',
        duration: 5000,
      });
      reset();
      setFiles([]);
      setUploadStates([]);
      onClose();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : '접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
      showToast({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrivacy = (e: React.MouseEvent) => {
    if (onOpenPrivacy) {
      e.preventDefault();
      onOpenPrivacy();
    }
  };

  // ----------------------------------------------------------------
  // 렌더
  // ----------------------------------------------------------------
  return (
    <div
      className={`modal-backdrop${open ? ' open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="b2bTitle"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div className="modal" role="document">
        <button
          type="button"
          className="modal-close"
          aria-label="닫기"
          onClick={handleClose}
          disabled={submitting}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <span className="modal-tag" id="b2bTitle">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          B2B 아웃바운드 접수
        </span>
        <p className="modal-desc">영업 담당자가 직접 접촉한 기업 정보를 접수합니다.</p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* 상담 서비스 (복수) */}
          <div className="modal-services">
            <div className="modal-form-label" style={{ marginBottom: 10 }}>
              상담 서비스<span className="req">*</span>{' '}
              <span className="form-services-hint" style={{ marginLeft: 6 }}>
                (복수 선택 가능)
              </span>
            </div>
            <div className="modal-services-grid">
              {SERVICE_LIST.map((svc) => {
                const checked = servicesValue.includes(svc.id);
                return (
                  <label key={svc.id} className="modal-svc-check">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleService(svc.id)}
                    />
                    <span className="modal-svc-box" />
                    <span className="modal-svc-text">{svc.label}</span>
                  </label>
                );
              })}
            </div>
            {errors.services && (
              <div className="modal-form-error" style={{ marginTop: 8 }}>
                {errors.services.message}
              </div>
            )}
          </div>

          {/* 기업 정보 */}
          <div className="modal-form-grid">
            <div className="modal-form-group">
              <label className="modal-form-label" htmlFor="b2b-company">
                회사명<span className="req">*</span>
              </label>
              <input
                id="b2b-company"
                className={`modal-form-input${errors.companyName ? ' invalid' : ''}`}
                placeholder="주식회사 OOO"
                {...register('companyName')}
              />
              {errors.companyName && (
                <div className="modal-form-error">{errors.companyName.message}</div>
              )}
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label" htmlFor="b2b-rep">
                대표자명<span className="req">*</span>
              </label>
              <input
                id="b2b-rep"
                className={`modal-form-input${errors.representativeName ? ' invalid' : ''}`}
                placeholder="홍길동"
                {...register('representativeName')}
              />
              {errors.representativeName && (
                <div className="modal-form-error">{errors.representativeName.message}</div>
              )}
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label" htmlFor="b2b-phone">
                연락처<span className="req">*</span>
              </label>
              <input
                id="b2b-phone"
                type="tel"
                inputMode="numeric"
                className={`modal-form-input${errors.contactPhone ? ' invalid' : ''}`}
                placeholder="010-0000-0000"
                {...register('contactPhone')}
                onChange={onPhoneChange}
              />
              {errors.contactPhone && (
                <div className="modal-form-error">{errors.contactPhone.message}</div>
              )}
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label" htmlFor="b2b-email">이메일</label>
              <input
                id="b2b-email"
                type="email"
                className={`modal-form-input${errors.contactEmail ? ' invalid' : ''}`}
                placeholder="name@company.com"
                {...register('contactEmail')}
              />
              {errors.contactEmail && (
                <div className="modal-form-error">{errors.contactEmail.message}</div>
              )}
            </div>

            <div className="modal-form-group full">
              <label className="modal-form-label" htmlFor="b2b-biz">
                사업자등록번호<span className="req">*</span>
              </label>
              <input
                id="b2b-biz"
                inputMode="numeric"
                className={`modal-form-input${
                  errors.businessNumber || bizVerify.isError ? ' invalid' : ''
                }`}
                placeholder="000-00-00000"
                {...register('businessNumber')}
                onChange={onBizChange}
                onBlur={bizVerify.onBlur}
              />
              {/* zod 형식/체크섬 에러가 우선 */}
              {errors.businessNumber && (
                <div className="modal-form-error">{errors.businessNumber.message}</div>
              )}
              {/* zod 에러가 없을 때만 국세청 검증 결과 표시 */}
              {!errors.businessNumber && bizVerify.status !== 'idle' && (
                <div
                  className={`modal-form-${
                    bizVerify.isError ? 'error' : 'hint'
                  }`}
                  style={
                    bizVerify.status === 'checking'
                      ? { color: '#94A3B8' }
                      : bizVerify.status === 'valid'
                      ? { color: '#10B981' }
                      : undefined
                  }
                >
                  {bizVerify.message}
                </div>
              )}
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label" htmlFor="b2b-emp">직원수</label>
              <input
                id="b2b-emp"
                className="modal-form-input"
                placeholder="예: 25명"
                {...register('employeeCount')}
              />
            </div>

            <div className="modal-form-group">
              <label className="modal-form-label" htmlFor="b2b-source">접촉 경로</label>
              <select
                id="b2b-source"
                className="modal-form-select"
                {...register('contactSource')}
              >
                <option value="">선택해 주세요</option>
                {B2B_SOURCE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          {/* 파일 첨부 */}
          <div className="file-drop">
            <div className="modal-form-label" style={{ marginBottom: 10 }}>서류 첨부</div>
            <label
              className={`file-drop-zone${dragging ? ' dragover' : ''}`}
              htmlFor="b2bFileInput"
              onDragOver={onDragOver}
              onDragEnter={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              <div className="file-drop-text">클릭하거나 파일을 드래그하세요</div>
              <div className="file-drop-hint">
                PDF·이미지·문서·zip · 파일당 10MB 이하 · 최대 {MAX_FILES}개
              </div>
              <input
                ref={fileInputRef}
                id="b2bFileInput"
                type="file"
                multiple
                accept={ACCEPT_ATTR}
                style={{ display: 'none' }}
                onChange={onFilePick}
              />
            </label>

            {files.length > 0 && (
              <div className="file-list">
                {files.map((f, idx) => {
                  const state = uploadStates[idx];
                  return (
                    <div key={`${f.name}-${idx}`} className="file-item">
                      <span className="file-item-name">📎 {f.name}</span>
                      <span className="file-item-size">{formatSize(f.size)}</span>
                      {state?.status === 'uploading' && (
                        <span className="file-item-progress">{state.progress}%</span>
                      )}
                      {state?.status === 'error' && (
                        <span className="file-item-status-error">실패</span>
                      )}
                      {!submitting && (
                        <button
                          type="button"
                          className="file-item-remove"
                          aria-label="삭제"
                          onClick={() => removeFile(idx)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 메모 */}
          <div className="modal-form-group full" style={{ marginBottom: 8 }}>
            <label className="modal-form-label" htmlFor="b2b-memo">메모</label>
            <textarea
              id="b2b-memo"
              className="modal-form-textarea"
              placeholder="특이사항이 있으면 입력해 주세요"
              {...register('memo')}
            />
          </div>

          {/* 개인정보 동의 */}
          <label className="modal-agree">
            <input type="checkbox" {...register('privacyConsent')} />
            <span>
              <strong style={{ color: '#fff' }}>[필수]</strong>{' '}
              <a
                href="/privacy"
                onClick={handlePrivacy}
                target={onOpenPrivacy ? undefined : '_blank'}
                rel="noreferrer"
              >
                개인정보 수집·이용
              </a>
              에 동의합니다. 수집항목: 회사명, 대표자명, 연락처, 이메일, 사업자등록번호.
              수집목적: 상담 서비스 제공. 보유기간: 상담 완료 후 3년 또는 동의 철회 시까지.
            </span>
          </label>
          {errors.privacyConsent && (
            <div className="modal-form-error" style={{ marginBottom: 12 }}>
              {errors.privacyConsent.message as string}
            </div>
          )}

          <button type="submit" className="modal-submit" disabled={submitting}>
            {submitting ? '접수 중...' : 'B2B 접수하기 →'}
          </button>
        </form>
      </div>
    </div>
  );
}
